import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, X, CheckCircle } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import countryList from 'react-select-country-list';
import Select from 'react-select';
import api from '../components/api';
import Navbar from '../components/accueil/Navbar';
import PageBanner from './Banner';
import Footer from '../components/accueil/Footer';
import ChatWidget from '../components/accueil/ChatWidget';
import useGlobale from '../components/global';



const InscriptionScreen = () => {
  const { authenticated, user, logout } = useGlobale();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const scrollToTop = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const [formData, setFormData] = useState({
    username: '',          // sera le numéro de téléphone
    email: '',
    telephone: '',
    password: '',
    confirmPassword: '',
    first_name: '',       // prénom
    last_name: '',        // nom
    date_naisance: '',    // correspond au champ du modèle
    sexe: '',
    nationalite: '',
    pays_residence: '',   // correspond au champ du modèle
    adresse: '',
    is_staff: 1,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Liste des pays
  const countries = useMemo(() => {
    return countryList().getData().map(country => ({
      value: country.value,
      label: country.label,
      flag: `https://flagcdn.com/24x18/${country.value.toLowerCase()}.png`
    }));
  }, []);

  // Styles pour le sélecteur de pays
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: '0.75rem',
      padding: '0.25rem',
      borderColor: state.isFocused ? '#f97316' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(249, 115, 22, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#f97316'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      backgroundColor: state.isSelected ? '#f97316' : state.isFocused ? '#fff7ed' : 'white',
      color: state.isSelected ? 'white' : '#374151',
      cursor: 'pointer'
    }),
    singleValue: (provided) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    })
  };

  const formatOptionLabel = ({ value, label, flag }) => (
    <div className="flex items-center gap-2">
      <img src={flag} alt={label} className="w-6 h-4 object-cover rounded-sm" />
      <span>{label}</span>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    scrollToTop();
    // Validations avant soumission
    if (!formData.telephone || formData.telephone.replace(/\D/g, '').length < 8) {
      setError("Veuillez entrer un numéro de téléphone valide");
      return;
    }

    if (!formData.email || !formData.email.includes('@')) {
      setError("Veuillez entrer une adresse email valide");
      return;
    }

    if (!formData.first_name || !formData.last_name) {
      setError("Les nom et prénom sont obligatoires");
      return;
    }

    if (!formData.date_naisance) {
      setError("La date de naissance est obligatoire");
      return;
    }

    if (!formData.nationalite || !formData.pays_residence) {
      setError("La nationalité et le pays de résidence sont obligatoires");
      return;
    }

    if (!formData.sexe) {
      setError("Veuillez sélectionner votre sexe");
      return;
    }

    // Vérification des mots de passe
    if (formData.password !== formData.confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (formData.password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setIsLoading(true);

    try {
      // Préparation des données pour l'API
      const userData = {
        username: formData.telephone.replace(/\D/g, ''),  // Le téléphone comme username
        email: formData.email,
        telephone: formData.telephone.replace(/\D/g, ''),
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        date_naisance: formData.date_naisance,
        sexe: formData.sexe,
        nationalite: formData.nationalite,
        pays_residence: formData.pays_residence,
        adresse: formData.adresse || '',
        is_staff: 1
      };

      // console.log('Données envoyées:', userData);
      const response = await api.post('/utilisateur/register/', userData);

      if (response.status === 201) {
        setSuccess("Inscription réussie ! Redirection vers la page de connexion...");
        scrollToTop();
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              success: true,
              message: "Compte créé avec succès ! Connectez-vous avec votre numéro de téléphone." 
            }
          });
        }, 3000);
      }
    } catch (error) {
      // console.error('Erreur:', error);
      scrollToTop();
      if (error.response) {
        if (error.response.data.username) {
          setError("Ce numéro de téléphone est déjà utilisé");
        } else if (error.response.data.email) {
          setError("Cette adresse email est déjà utilisée");
        } else if (error.response.data.error) {
          setError(error.response.data.error);
        } else {
          setError("Une erreur est survenue lors de l'inscription");
        }
      } else {
        setError("Erreur de connexion au serveur");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <Navbar authenticated={authenticated} user={user} logout={logout} />
      <PageBanner currentPage="Inscription" />
      <div className="min-h-[calc(100vh-450px)] flex items-center justify-center bg-gray-50 py-8">
        <div className="max-w-3xl w-full mx-4 bg-white p-8 rounded-2xl shadow-xl">
          {/* En-tête */}
          <div className="text-center mb-8"  ref={formRef}>
            <h2 className="text-2xl font-bold text-gray-900">
              Créer un compte
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Avez-vous déjà un compte ?{' '}
              <a href="/login" className="text-brand-600 hover:text-brand-700">
                Se connecter →
              </a>
            </p>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="relative mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              <button 
                onClick={() => setError(null)}
                className="absolute top-2 right-2"
              >
                <X className="h-4 w-4" />
              </button>
              {error}
            </div>
          )}
            {success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email et Téléphone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                           focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="exemple@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  country={'ne'}
                  value={formData.telephone}
                  onChange={phone => setFormData({ ...formData, telephone: phone })}
                  inputProps={{
                    required: true,
                    name: 'telephone',
                  }}
                  containerClass="!w-full"
                  inputClass="!w-full !h-[46px] !text-base"
                  buttonClass="!h-[46px]"
                  preferredCountries={['ne', 'bj', 'bf', 'ml', 'ng', 'td']}
                  searchPlaceholder="Rechercher un pays"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ce numéro servira d'identifiant de connexion
                </p>
              </div>
            </div>

            {/* Nom et Prénom */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                           focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom(s) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                           focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            {/* Date de naissance et Sexe */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de naissance <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date_naisance"
                  value={formData.date_naisance}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                           focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sexe <span className="text-red-500">*</span>
                </label>
                <select
                  name="sexe"
                  value={formData.sexe}
                  onChange={handleChange}
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                           focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">Sélectionnez</option>
                  <option value="H">Homme</option>
                  <option value="F">Femme</option>
                </select>
              </div>
            </div>

            {/* Nationalité et Pays de résidence */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nationalité <span className="text-red-500">*</span>
                </label>
                <Select
                  options={countries}
                  value={countries.find(country => country.value === formData.nationalite)}
                  onChange={(option) => setFormData({ ...formData, nationalite: option.value })}
                  styles={customStyles}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Sélectionnez votre nationalité"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pays de résidence <span className="text-red-500">*</span>
                </label>
                <Select
                  options={countries}
                  value={countries.find(country => country.value === formData.pays_residence)}
                  onChange={(option) => setFormData({ ...formData, pays_residence: option.value })}
                  styles={customStyles}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Sélectionnez votre pays"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isSearchable={true}
                />
              </div>
            </div>

            {/* Adresse */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse
              </label>
              <textarea
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                rows="3"
                className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                         focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Votre adresse de résidence"
              />
            </div>

            {/* Mots de passe */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                             focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? 
                      <EyeOff className="h-5 w-5 text-gray-400" /> :
                      <Eye className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 6 caractères
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le mot de passe <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl
                             focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? 
                      <EyeOff className="h-5 w-5 text-gray-400" /> :
                      <Eye className="h-5 w-5 text-gray-400" />
                    }
                  </button>
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 
                         hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 
                         focus:ring-gray-500 transition-all duration-200 ease-in-out"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isLoading || !formData.password || !formData.confirmPassword}
                className={`px-6 py-3 bg-brand-600 text-white rounded-xl
                          hover:bg-brand-700 focus:outline-none focus:ring-2 
                          focus:ring-offset-2 focus:ring-brand-500
                          transition-all duration-200 ease-in-out
                          ${(isLoading || !formData.password || !formData.confirmPassword) ? 'opacity-80 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Créer mon compte'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
      <ChatWidget />

      {/* Styles globaux */}
      <style jsx global>{`
        /* Styles pour react-phone-input-2 */
        .react-tel-input .form-control {
          width: 100% !important;
          height: 46px !important;
          border-radius: 0.75rem !important;
          font-size: 1rem !important;
        }
        
        .react-tel-input .flag-dropdown {
          border-radius: 0.75rem 0 0 0.75rem !important;
          border-color: #e5e7eb !important;
        }
        
        .react-tel-input .selected-flag {
          border-radius: 0.75rem 0 0 0.75rem !important;
          background-color: transparent !important;
        }

        .react-tel-input .selected-flag:hover,
        .react-tel-input .selected-flag:focus {
          background-color: transparent !important;
        }

        .react-tel-input .country-list {
          border-radius: 0.75rem !important;
          margin-top: 8px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        /* Styles pour react-select */
        .react-select__menu {
          border-radius: 0.75rem !important;
          overflow: hidden;
          margin-top: 0.5rem !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        .react-select__option {
          padding: 0.75rem !important;
        }

        .react-select__control--is-focused {
          border-color: #f97316 !important;
          box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2) !important;
        }

        .react-select__indicator-separator {
          display: none;
        }

        .react-select__control {
          border-color: #e5e7eb !important;
        }

        .react-select__value-container {
          padding: 0.375rem 0.75rem !important;
        }
      `}</style>
    </>
  );
};

export default InscriptionScreen;